import { get_active_organization_id } from "@/utils/organization_profile";
import { load_google_places_script } from "@/components/Delivery/delivery_helpers";
import {
  request_create_organization_company,
  request_delete_organization_company,
  request_organization_companies,
  request_set_organization_company_status,
  request_update_organization_company,
} from "./organization_members_api";

function empty_company() {
  return {
    company_name: "",
    description: "",
    email: "",
    tell_number: "",
    location: "",
  };
}

function companies_from_response(response) {
  if (response?.status !== "success") return [];
  return response.companies || [];
}

function company_creation_error(error) {
  return error?.responseJSON?.message
    || error?.message
    || "Could not create company.";
}

function member_belongs_to_company(member, company_id) {
  return Number(member.company_id) === Number(company_id);
}

function member_has_role(member, role) {
  return String(member.role || "").toLowerCase() === role;
}

function member_invitation_is_accepted(member) {
  return String(member.invitation_status || "").toLowerCase() === "accepted";
}

function company_manager_name(manager) {
  const full_name = [manager?.first_name, manager?.last_name]
    .filter(Boolean)
    .join(" ");
  return [full_name, manager?.email, "Not assigned"].find(Boolean);
}

function ensure_company_created(response) {
  if (response?.status !== "success") {
    throw new Error(response?.message);
  }
}

function company_created_message(response) {
  return response.message || "Company created";
}

function show_company_invitation_modal() {
  $("#invite_organization_member_modal").modal("show");
}

export default {
  open_company_form() {
    this.reset_company_form();
    this.$nextTick(() => this.initialize_company_location_autocomplete());
  },
  initialize_company_location_autocomplete() {
    return load_google_places_script().then(() => {
      const active_modal = document.querySelector("#add_organization_company_modal.show");
      const input = active_modal?.querySelector("#company_location")
        || this.$refs.companyLocationInput;
      if (!input || !window.google?.maps?.places) return;
      if (this.company_location_autocomplete_input === input) return;

      this.company_location_autocomplete = new window.google.maps.places.Autocomplete(input);
      this.company_location_autocomplete_input = input;
      this.company_location_autocomplete.addListener("place_changed", () => {
        const place = this.company_location_autocomplete.getPlace();
        if (!place?.geometry?.location) return;
        this.new_company.location = input.value;
      });
    }).catch(() => {
      this.show_error("Location suggestions are currently unavailable");
    });
  },
  async fetch_organization_companies() {
    const organization_id = get_active_organization_id();
    if (!organization_id || !this.can_manage_companies) return;
    this.destroy_companies_datatable();
    this.is_loading_companies = true;
    try {
      const response = await request_organization_companies({ organization_id });
      this.organization_companies = companies_from_response(response);
    } catch {
      this.show_error("Could not load organisation companies");
    } finally {
      this.is_loading_companies = false;
      this.refresh_companies_datatable();
    }
  },
  reset_company_form() {
    this.editing_company_id = null;
    this.new_company = empty_company();
    this.company_errors = {};
  },
  edit_organization_company(company) {
    this.editing_company_id = company.id;
    this.new_company = {
      company_name: company.company_name || "",
      description: company.description || "",
      email: company.email || "",
      tell_number: company.tell_number || "",
      location: company.location || "",
    };
    this.company_errors = {};
    $("#add_organization_company_modal").modal("show");
    this.$nextTick(() => this.initialize_company_location_autocomplete());
  },
  submit_company_form() {
    return this.editing_company_id
      ? this.update_organization_company()
      : this.create_organization_company();
  },
  validate_company_form() {
    this.company_errors = {};
    if (!String(this.new_company.company_name || "").trim()) {
      this.company_errors.company_name = "Company name is required.";
    }
    return !Object.keys(this.company_errors).length;
  },
  async create_organization_company() {
    if (!this.validate_company_form()) return;
    this.is_creating_company = true;
    try {
      const response = await request_create_organization_company({
        organization_id: get_active_organization_id(),
        ...this.new_company,
      });
      ensure_company_created(response);
      this.destroy_companies_datatable();
      this.organization_companies.push({ ...response.company, member_count: 0 });
      this.refresh_companies_datatable();
      this.reset_company_form();
      $("#add_organization_company_modal").modal("hide");
      this.show_success(company_created_message(response));
      this.reset_invite_form();
      this.selected_company_id = response.company.id;
      setTimeout(show_company_invitation_modal, 150);
    } catch (error) {
      this.company_errors.form = company_creation_error(error);
    } finally {
      this.is_creating_company = false;
    }
  },
  async update_organization_company() {
    if (!this.validate_company_form()) return;
    this.is_updating_company = true;
    try {
      const response = await request_update_organization_company({
        organization_id: get_active_organization_id(),
        company_id: this.editing_company_id,
        ...this.new_company,
      });
      ensure_company_created(response);
      this.destroy_companies_datatable();
      const index = this.organization_companies.findIndex((company) => company.id === this.editing_company_id);
      if (index !== -1) this.organization_companies.splice(index, 1, response.company);
      this.refresh_companies_datatable();
      $("#add_organization_company_modal").modal("hide");
      this.show_success(response.message || "Company updated");
      this.reset_company_form();
    } catch (error) {
      this.company_errors.form = company_creation_error(error);
    } finally {
      this.is_updating_company = false;
    }
  },
  toggle_organization_company_status(company) {
    return request_set_organization_company_status({
      organization_id: get_active_organization_id(),
      company_id: company.id,
      is_active: company.is_active === false,
    }).then((response) => {
      ensure_company_created(response);
      this.destroy_companies_datatable();
      Object.assign(company, response.company);
      this.refresh_companies_datatable();
      this.show_success(response.message || "Company status updated");
    }).catch((error) => {
      this.show_error(company_creation_error(error));
    });
  },
  async delete_organization_company(company) {
    const confirmed = window.confirm(`Delete ${company.company_name}? This permanently removes the company and its members.`);
    if (!confirmed) return;
    try {
      const response = await request_delete_organization_company({
        organization_id: get_active_organization_id(),
        company_id: company.id,
      });
      ensure_company_created(response);
      this.destroy_companies_datatable();
      this.destroy_members_datatable();
      this.destroy_invitations_datatable();
      this.organization_companies = this.organization_companies.filter((item) => item.id !== company.id);
      this.organization_members = this.organization_members.filter((member) => Number(member.company_id) !== Number(company.id));
      this.refresh_companies_datatable();
      this.refresh_members_datatable();
      this.refresh_invitations_datatable();
      this.show_success(response.message || "Company deleted");
    } catch (error) {
      this.show_error(company_creation_error(error));
    }
  },
  get_company_name(company_id) {
    if (!company_id || Number(company_id) === Number(get_active_organization_id())) {
      return this.organization.company_name;
    }
    return this.organization_companies.find((company) => Number(company.id) === Number(company_id))?.company_name || "Organisation";
  },
  get_company_manager(company_id) {
    const manager = this.organization_members.find((member) =>
      member_belongs_to_company(member, company_id) && member_has_role(member, "manager")
    );
    return company_manager_name(manager);
  },
  get_company_employee_count(company_id) {
    return this.organization_members.filter((member) =>
      member_belongs_to_company(member, company_id)
      && !member_has_role(member, "manager")
      && member_invitation_is_accepted(member)
    ).length;
  },
};
